import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, HelpCircle, X, Check, Play, Pause, Moon, Volume2, VolumeX, SkipForward } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { useProtocolAudio } from "../hooks/useProtocolAudio";
import { useMicBreath } from "../hooks/useMicBreath";
import { useWakeLock } from "../hooks/useWakeLock";
import { playBell } from "../utils/bell";
import { STEP_GUIDES } from "../utils/stepGuides";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROTOCOL_STEPS = [
  { id:"kapalbhati", name:"Skull-Shining Breath", sanskrit:"Kapalbhati", duration:30, phase:"reset", phaseLabel:"RESET", benefit:"Norepinephrine spike · wake the system", breathHint:"Sharp exhales · belly pumps in" },
  { id:"nadi_shodhana", name:"Alternate Nostril", sanskrit:"Nadi Shodhana", duration:120, phase:"reset", phaseLabel:"RESET", benefit:"ANS balance · coherence", breathHint:"Inhale 4 · exhale 4 · alternate", nostril:true },
  { id:"bhramari", name:"Humming Breath", sanskrit:"Bhramari", duration:90, phase:"reset", phaseLabel:"RESET", benefit:"HRV spike · vagal activation", breathHint:"Inhale · exhale with throat hum" },
  { id:"rechaka", name:"Extended Exhale", sanskrit:"Rechaka", duration:60, phase:"reset", phaseLabel:"RESET", benefit:"Parasympathetic lock · deep calm", breathHint:"Inhale 4 · exhale 8" },
  { id:"trataka", name:"Focused Gazing", sanskrit:"Trataka", duration:180, phase:"lock", phaseLabel:"LOCK", benefit:"Acetylcholine surge · neuroplastic window", breathHint:"Natural breath · gaze steady" },
  { id:"yoga_nidra", name:"Deep Rest Protocol", sanskrit:"Yoga Nidra", duration:420, phase:"install", phaseLabel:"INSTALL", benefit:"BDNF release · consolidate new wiring", breathHint:"Eyes closed · voice guided" },
];

const PHASES = {
  reset:   { duration:300, color:"#4A90D9", label:"RESET" },
  lock:    { duration:180, color:"#C9A84C", label:"LOCK" },
  install: { duration:420, color:"#4CAF7A", label:"INSTALL" },
};

const NSDR_OPTIONS = [
  { label:"7 min",  value:420, desc:"Standard" },
  { label:"10 min", value:600, desc:"Extended" },
  { label:"15 min", value:900, desc:"Deep" },
];

const getPhaseColor = (phase) => ({ reset:"#4A90D9", lock:"#C9A84C", install:"#4CAF7A" }[phase] || "#4A90D9");
const formatTime = (s) => `${Math.floor(s/60)}:${String(Math.floor(s%60)).padStart(2,"0")}`;

const SEEN_KEY = "clarityos_seen_guides";
const getSeenGuides = () => { try { return JSON.parse(localStorage.getItem(SEEN_KEY)||"[]"); } catch { return []; } };
const markGuideSeen = (id) => { const s=getSeenGuides(); if(!s.includes(id)) localStorage.setItem(SEEN_KEY,JSON.stringify([...s,id])); };


// ── Nostril Air-Flow Visualiser ────────────────────────────────────────────
//
// Renders a horizontal "breath channel" — a narrow oval that contracts inward
// on inhale (air being pulled in) and expands outward on exhale (air leaving).
//
// For Nadi Shodhana: the channel is split left / right. Only the active nostril
// animates. The blocked nostril stays flat. SWITCH moment: both flatten.
//
// Props:
//   amplitude    — 0-1 from mic (drives intensity / glow)
//   isActive     — mic is running
//   cyclePhase   — "inhale" | "exhale" | "switch" (driven by step timing)
//   nostrilSide  — "left" | "right" | null (null = both nostrils active)
//   isSwitch     — boolean (Nadi Shodhana switch moment)
//   color        — phase colour hex

// ── LotusBreathe — imported from shared utility ───────────────────────────────
import { N_PETALS, NADI_LEFT, NADI_RIGHT, easeInOut, renderLotus } from "../utils/lotusBreathe";

const NostrilFlow = ({ stepId, isPlaying, cyclePhase, nostrilSide, isSwitch, color, amplitude }) => {
  const canvasRef = useRef(null);
  // Persistent animation state — survives prop changes
  const stateRef  = useRef({ rot: 0, lastT: 0, raf: null, openness: 0.10, nadiOpenL: 0.10, nadiOpenR: 0.10 });
  const propsRef  = useRef({ cyclePhase, nostrilSide, isSwitch, isPlaying, amplitude: 0 });

  // Keep propsRef current so RAF loop reads latest values without restarting
  useEffect(() => {
    propsRef.current = { cyclePhase, nostrilSide, isSwitch, isPlaying, amplitude: amplitude || 0 };
  });

  // Pill label
  let pillLabel = "ready";
  if (isPlaying) {
    if (isSwitch)                     pillLabel = "switch";
    else if (nostrilSide)             pillLabel = cyclePhase + " · " + nostrilSide;
    else if (cyclePhase === "exhale") pillLabel = "exhale";
    else                              pillLabel = "inhale";
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const st = stateRef.current;
    st.lastT = performance.now();

    // Breath timing per step
    const timings = {
      kapalbhati:    { inMs: 900,  exMs: 900  },
      bhramari:      { inMs: 4000, exMs: 8000 },
      rechaka:       { inMs: 5000, exMs: 10000 },
      nadi_shodhana: { inMs: 4500, exMs: 4500 },
    };
    const { inMs, exMs } = timings[stepId] || { inMs: 4500, exMs: 4500 };
    const cycle = inMs + exMs;

    // ── Openness range — calmer, less exaggerated ───────────────────────
    // Max reduced from 0.78 → 0.58; rest raised slightly so petals never
    // fully collapse; lerp speeds halved throughout.
    const OPEN_MAX   = 0.58;
    const OPEN_REST  = 0.13;
    const OPEN_GHOST = 0.08; // inactive Nadi side

    function frame(now) {
      const dt = Math.min((now - st.lastT) / 1000, 0.05);
      st.lastT = now;

      const { cyclePhase: cp, nostrilSide: ns, isSwitch: sw, isPlaying: ip, amplitude: amp } = propsRef.current;

      let targetOpenness = st.openness;
      let petalStates    = null;

      if (!ip) {
        // ── PAUSED / INTRO: drift quietly to resting, freeze rotation ──
        targetOpenness = OPEN_REST;
        // Lerp toward rest (slow drain)
        st.openness = st.openness + (targetOpenness - st.openness) * 0.018;
        renderLotus(canvas, st.openness, st.rot, null, color, 0);
        st.raf = requestAnimationFrame(frame);
        return;
      }

      if (stepId === "nadi_shodhana") {
        if (sw) {
          // Switch moment: both sides close together
          st.nadiOpenL = st.nadiOpenL + (OPEN_REST - st.nadiOpenL) * 0.025;
          st.nadiOpenR = st.nadiOpenR + (OPEN_REST - st.nadiOpenR) * 0.025;
        } else {
          const side = ns || "left";
          // When mic is active, drive petal openness from actual breath amplitude
          const micDriven = amp > 0.03;
          const ampTarget = OPEN_REST + amp * (OPEN_MAX - OPEN_REST);
          const timedTarget = cp === "inhale" ? OPEN_MAX : OPEN_REST + 0.04;
          const activeTarget = micDriven ? ampTarget : timedTarget;
          const activeSpeed  = micDriven ? 0.06 : 0.020;
          const ghostSpeed   = 0.015;
          if (side === "left") {
            st.nadiOpenL = st.nadiOpenL + (activeTarget    - st.nadiOpenL) * activeSpeed;
            st.nadiOpenR = st.nadiOpenR + (OPEN_GHOST      - st.nadiOpenR) * ghostSpeed;
          } else {
            st.nadiOpenR = st.nadiOpenR + (activeTarget    - st.nadiOpenR) * activeSpeed;
            st.nadiOpenL = st.nadiOpenL + (OPEN_GHOST      - st.nadiOpenL) * ghostSpeed;
          }
          st.nadiOpenL = Math.max(OPEN_GHOST, Math.min(OPEN_MAX, st.nadiOpenL));
          st.nadiOpenR = Math.max(OPEN_GHOST, Math.min(OPEN_MAX, st.nadiOpenR));
        }

        // ── Petal layout (viewed from front, lotus upright) ──────────
        // Petal index 0 = top (12 o'clock), going clockwise:
        //   0 = top,       1 = top-right,    2 = bottom-right
        //   3 = bottom-left, 4 = top-left
        // Left channel  → petals 3 (bottom-left) + 4 (top-left)
        // Right channel → petals 1 (top-right)  + 2 (bottom-right)
        // Top petal (0) = active side — so it reflects whichever nostril is working
        const activeSide = ns || "left";
        petalStates = [
          activeSide === "left" ? st.nadiOpenL : st.nadiOpenR, // petal 0 = top, mirrors active
          st.nadiOpenR, // petal 1 top-right
          st.nadiOpenR, // petal 2 bottom-right
          st.nadiOpenL, // petal 3 bottom-left
          st.nadiOpenL, // petal 4 top-left
        ];
        targetOpenness = Math.max(st.nadiOpenL, st.nadiOpenR);

      } else {
        // Other RESET steps: when mic picks up breath, use amplitude to drive openness
        const micDriven = amp > 0.03;
        const isInh = cp === "inhale";
        let target;
        if (micDriven) {
          // Breath amplitude directly drives petal openness
          target = OPEN_REST + amp * (OPEN_MAX - OPEN_REST);
          // Bhramari: add gentle tremor on exhale (humming vibration)
          if (stepId === "bhramari" && !isInh) {
            target = Math.max(0, Math.min(OPEN_MAX, target + Math.sin(now / 200) * 0.015));
          }
        } else {
          // Fallback: timer-driven when mic isn't picking up signal
          target = isInh ? OPEN_MAX : OPEN_REST;
          if (stepId === "bhramari" && !isInh) {
            target = Math.max(0, Math.min(OPEN_MAX, target + Math.sin(now / 300) * 0.008));
          }
        }
        targetOpenness = target;
      }

      // ── Lerp — faster when mic-driven for real-time responsiveness ─────
      if (stepId !== "nadi_shodhana") {
        const micDriven = amp > 0.03;
        // Mic-driven: faster lerp for real breath tracking; timer: gentle transitions
        const lerpSpeed = micDriven
          ? (stepId === "kapalbhati" ? 0.12 : 0.08)
          : (stepId === "kapalbhati" ? 0.032 : 0.016);
        st.openness = st.openness + (targetOpenness - st.openness) * lerpSpeed;
        st.openness = Math.max(OPEN_REST - 0.04, Math.min(OPEN_MAX, st.openness));
      } else {
        st.openness = targetOpenness;
      }

      // ── Slow rotation — only when playing, not switching ───────────
      if (!sw) {
        const rotSpeeds = { kapalbhati: 0.18, bhramari: 0.07, rechaka: 0.06, nadi_shodhana: 0.07 };
        const spd = rotSpeeds[stepId] || 0.07;
        const goingIn = stepId === "nadi_shodhana"
          ? cp === "inhale"
          : (now % cycle) < inMs;
        st.rot += (goingIn ? 1 : -0.25) * dt * spd;
      }

      renderLotus(canvas, st.openness, st.rot, petalStates, color, amp);
      st.raf = requestAnimationFrame(frame);
    }

    st.raf = requestAnimationFrame(frame);
    return () => { if (st.raf) cancelAnimationFrame(st.raf); };
  }, [stepId, color]); // only restart on step/color change; live props via ref

  return (
    <div className="w-full flex flex-col items-center" style={{ gap: 8, marginBottom: 8 }}>
      <div className="flex items-center gap-[6px] px-3 py-[5px] rounded-full"
        style={{
          background: isPlaying ? color + "14" : "rgba(255,255,255,0.04)",
          border: "1px solid " + (isPlaying ? color + "30" : "rgba(255,255,255,0.07)"),
          transition: "all 0.5s", minWidth: 88, justifyContent: "center"
        }}>
        <div style={{
          width:5, height:5, borderRadius:"50%",
          background: isPlaying ? color : "rgba(255,255,255,0.18)",
          animation: isPlaying ? "lotusDotPulse 1.4s ease-in-out infinite" : "none",
          boxShadow: isPlaying ? "0 0 5px " + color + "90" : "none",
          transition:"all 0.5s", flexShrink:0
        }}/>
        <span className="font-mono text-[9px] tracking-[0.16em] uppercase"
          style={{ color: isPlaying ? color + "CC" : "rgba(237,232,223,0.2)", transition:"color 0.5s" }}>
          {pillLabel}
        </span>
      </div>
      <canvas ref={canvasRef} width={180} height={180} style={{ width:180, height:180 }} />
      <style>{`
        @keyframes lotusDotPulse {
          0%,100%{opacity:0.4;transform:scale(1);}
          50%{opacity:1;transform:scale(1.6);}
        }
      `}</style>
    </div>
  );
};

// ── Amber transition pulse ─────────────────────────────────────────────────
const AmberPulse = ({ active }) => (
  <AnimatePresence>
    {active&&<motion.div initial={{opacity:0,scale:.5}} animate={{opacity:[1,0],scale:[1,2.5]}} exit={{opacity:0}} transition={{duration:.8,ease:"easeOut"}} className="fixed inset-0 pointer-events-none flex items-center justify-center z-50">
      <div className="w-3 h-3 rounded-full" style={{background:"#C9A84C"}}/>
    </motion.div>}
  </AnimatePresence>
);

// ── Guide Sheet ────────────────────────────────────────────────────────────
const GuideSheet = ({ stepId, onClose }) => {
  const guide = STEP_GUIDES[stepId];
  if (!guide) return null;
  return (
    <motion.div initial={{y:"100%"}} animate={{y:0}} exit={{y:"100%"}} transition={{type:"spring",damping:32,stiffness:280}}
      className="fixed inset-x-0 bottom-0 z-50 rounded-t-[20px] p-6 pb-10"
      style={{background:"#1c2128",border:"1px solid rgba(255,255,255,0.08)",borderBottom:"none"}}
      onClick={e=>e.stopPropagation()}>
      <div className="w-10 h-1 rounded-full mx-auto mb-5" style={{background:"rgba(255,255,255,0.12)"}}/>
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="font-mono text-[9px] tracking-[0.18em] uppercase mb-1" style={{color:"#C9A84C"}}>How to practice</p>
          <h3 className="text-[20px]" style={{fontFamily:"'DM Serif Display',serif",color:"#e8e2d6"}}>{guide.title}</h3>
          <p className="text-[12px] italic" style={{fontFamily:"'DM Serif Display',serif",color:"rgba(237,232,223,0.5)"}}>{guide.sanskrit}</p>
        </div>
        <button onClick={onClose} className="w-7 h-7 rounded-full flex items-center justify-center mt-1" style={{background:"rgba(255,255,255,0.06)"}}>
          <X size={13} style={{color:"rgba(237,232,223,0.5)"}}/>
        </button>
      </div>
      <div className="space-y-3 mb-5">
        {guide.lines.map((line,i)=>(
          <div key={i} className="flex gap-3 items-start">
            <span className="font-mono text-[10px] mt-[2px] min-w-[16px]" style={{color:"#C9A84C"}}>{i+1}</span>
            <p className="text-[13px] leading-[1.6]" style={{color:"rgba(237,232,223,0.75)"}}>{line}</p>
          </div>
        ))}
      </div>
      <div className="rounded-[8px] p-3" style={{background:"rgba(201,168,76,0.06)",border:"1px solid rgba(201,168,76,0.12)"}}>
        <p className="text-[11px] leading-[1.6]" style={{color:"rgba(237,232,223,0.5)",fontStyle:"italic"}}>{guide.tip}</p>
      </div>
      <button onClick={onClose} className="w-full mt-5 py-3 rounded-[10px] font-mono text-[11px] tracking-[0.12em] uppercase" style={{background:"#C9A84C",color:"#0D0F14",fontWeight:600}}>
        Got it
      </button>
    </motion.div>
  );
};

// ── NSDR Duration Selector ─────────────────────────────────────────────────
const NSDRSelector = ({ onSelect }) => (
  <motion.div initial={{opacity:0,y:16}} animate={{opacity:1,y:0}} className="flex flex-col items-center px-7 py-10 gap-4">
    <p className="font-mono text-[9px] tracking-[0.22em] uppercase mb-1" style={{color:"#4CAF7A"}}>Install · Yoga Nidra</p>
    <h2 className="text-[24px] text-center mb-2" style={{fontFamily:"'DM Serif Display',serif",color:"#e8e2d6"}}>How long to rest?</h2>
    <p className="text-[12px] text-center mb-4" style={{color:"rgba(237,232,223,0.5)",maxWidth:260,lineHeight:1.6}}>
      You will be guided by voice throughout. A bell signals the end.
    </p>
    <div className="flex gap-3 w-full max-w-[320px]">
      {NSDR_OPTIONS.map(opt=>(
        <button key={opt.value} onClick={()=>onSelect(opt.value)}
          className="flex-1 py-5 rounded-[12px] flex flex-col items-center gap-1 transition-all"
          style={{background:"rgba(76,175,122,0.08)",border:"1px solid rgba(76,175,122,0.2)"}}>
          <Moon size={16} style={{color:"#4CAF7A",marginBottom:4}}/>
          <span className="font-mono text-[15px]" style={{color:"#e8e2d6",fontWeight:500}}>{opt.label}</span>
          <span className="font-mono text-[9px] tracking-[0.1em] uppercase" style={{color:"rgba(237,232,223,0.38)"}}>{opt.desc}</span>
        </button>
      ))}
    </div>
  </motion.div>
);

// ── Trataka Candle ─────────────────────────────────────────────────────────
const TratakaCandle = () => (
  <div className="relative w-[160px] h-[240px] mb-5 flex flex-col items-center justify-end">
    <svg viewBox="0 0 120 180" className="w-full h-full" style={{overflow:"visible"}}>
      <defs>
        <radialGradient id="fC" cx="50%" cy="80%" r="50%">
          <stop offset="0%"   stopColor="#fff8e1" stopOpacity="1"/>
          <stop offset="30%"  stopColor="#C9A84C" stopOpacity="0.95"/>
          <stop offset="70%"  stopColor="#D9844A" stopOpacity="0.7"/>
          <stop offset="100%" stopColor="#D94A4A" stopOpacity="0"/>
        </radialGradient>
        <radialGradient id="fO" cx="50%" cy="60%" r="50%">
          <stop offset="0%"   stopColor="#C9A84C" stopOpacity="0.35"/>
          <stop offset="100%" stopColor="#C9A84C" stopOpacity="0"/>
        </radialGradient>
        <filter id="fG" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="blur"/>
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        <linearGradient id="wG" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor="#1c2128"/>
          <stop offset="50%"  stopColor="#2a3140"/>
          <stop offset="100%" stopColor="#1c2128"/>
        </linearGradient>
      </defs>
      {/* Outer glow — wider, taller */}
      <ellipse cx="60" cy="88" rx="32" ry="28" fill="url(#fO)">
        <animate attributeName="rx" values="32;38;30;35;32" dur="3s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.6;1;0.7;0.9;0.6" dur="3s" repeatCount="indefinite"/>
      </ellipse>
      {/* Outer flame shape — taller (tip at y=18) */}
      <path filter="url(#fG)" fill="url(#fC)" opacity="0.5">
        <animate attributeName="d" dur="2.1s" repeatCount="indefinite"
          values="M60,98 C44,86 36,68 40,50 C44,34 60,22 60,18 C60,22 76,34 80,50 C84,68 76,86 60,98Z;
                  M60,98 C42,84 34,65 38,48 C42,32 59,20 60,16 C61,20 78,32 82,48 C86,65 78,84 60,98Z;
                  M60,98 C46,87 38,70 42,52 C46,36 61,24 60,19 C59,24 74,36 78,52 C82,70 74,87 60,98Z;
                  M60,98 C44,86 36,68 40,50 C44,34 60,22 60,18 C60,22 76,34 80,50 C84,68 76,86 60,98Z"/>
      </path>
      {/* Inner flame — core, taller */}
      <path filter="url(#fG)" fill="url(#fC)">
        <animate attributeName="d" dur="1.8s" repeatCount="indefinite"
          values="M60,97 C50,88 46,74 48,60 C50,48 60,36 60,30 C60,36 70,48 72,60 C74,74 70,88 60,97Z;
                  M60,97 C48,86 45,72 47,58 C49,46 60,34 60,28 C60,34 71,46 73,58 C75,72 72,86 60,97Z;
                  M60,97 C51,89 47,75 49,61 C51,49 61,37 60,31 C59,37 69,49 71,61 C73,75 69,89 60,97Z;
                  M60,97 C50,88 46,74 48,60 C50,48 60,36 60,30 C60,36 70,48 72,60 C74,74 70,88 60,97Z"/>
      </path>
      {/* Bright core tip */}
      <ellipse cx="60" cy="72" rx="6" ry="10" fill="#fff8e1" opacity="0.9">
        <animate attributeName="cy" values="72;70;73;71;72" dur="1.8s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.9;1;0.8;0.95;0.9" dur="1.8s" repeatCount="indefinite"/>
      </ellipse>
      {/* Wick */}
      <line x1="60" y1="97" x2="60" y2="103" stroke="#4a3520" strokeWidth="1.5" strokeLinecap="round"/>
      {/* Candle body */}
      <rect x="46" y="103" width="28" height="58" rx="3" fill="url(#wG)" stroke="rgba(255,255,255,0.06)" strokeWidth="0.5"/>
      <rect x="49" y="106" width="4" height="50" rx="2" fill="rgba(255,255,255,0.05)"/>
      {/* Base shadow */}
      <ellipse cx="60" cy="161" rx="22" ry="4" fill="#1c2128" stroke="rgba(255,255,255,0.08)" strokeWidth="0.5"/>
      <ellipse cx="60" cy="163" rx="30" ry="6" fill="#C9A84C" opacity="0.06">
        <animate attributeName="rx" values="30;35;28;32;30" dur="3s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.06;0.1;0.05;0.08;0.06" dur="3s" repeatCount="indefinite"/>
      </ellipse>
    </svg>
  </div>
);

// ── NSDR Visual ────────────────────────────────────────────────────────────
const NSDRVisual = ({ color }) => (
  <div className="flex flex-col items-center gap-3 mb-5">
    <div className="relative w-[80px] h-[80px] flex items-center justify-center">
      {[1,2,3].map(i=>(
        <div key={i} className="absolute rounded-full" style={{width:80*i/3,height:80*i/3,border:`1px solid ${color}`,opacity:0.08+(0.06*(4-i)),animation:`nsdrR ${3+i}s ease-in-out ${i*.8}s infinite`}}/>
      ))}
      <Moon size={22} style={{color,opacity:0.7}}/>
    </div>
    <style>{`@keyframes nsdrR{0%,100%{transform:scale(1);opacity:.12}50%{transform:scale(1.08);opacity:.22}}`}</style>
  </div>
);

// ── Main Protocol Component ────────────────────────────────────────────────
export const Protocol = ({ user }) => {
  const navigate = useNavigate();

  const [sessionState, setSessionState] = useState("intro");
  const [currentStep, setCurrentStep]   = useState(0);
  const [nsdrDuration, setNsdrDuration] = useState(420);
  const [stepTimeLeft, setStepTimeLeft] = useState(PROTOCOL_STEPS[0].duration);
  const [sessionElapsed, setSessionElapsed] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [startTime, setStartTime]       = useState(null);
  const [isIntroPlaying, setIsIntroPlaying] = useState(false);
  const isIntroPlayingRef = useRef(false);
  const [isAudioLocked, setIsAudioLocked] = useState(false);
  const [nostrilSide, setNostrilSide] = useState("left");
  const [isSwitch, setIsSwitch]       = useState(false);
  const [breathCyclePhase, setBreathCyclePhase] = useState("inhale"); // inhale | exhale
  const [showGuide, setShowGuide]     = useState(false);
  const [amberPulse, setAmberPulse]   = useState(false);
  const [isMuted, setIsMuted]         = useState(false);
  const [continuousMode, setContinuousMode] = useState(false);
  const [countdownNum, setCountdownNum] = useState(null); // 3 → 2 → 1 → null
  const [showJournalForm, setShowJournalForm] = useState(false);
  const [journalEntry, setJournalEntry]       = useState("");
  const [savingJournal, setSavingJournal]     = useState(false);

  const breathTimersRef = useRef([]);
  const guideOpenRef    = useRef(false); // timer pauses while guide is showing
  const isCompletingRef = useRef(false); // prevents double-fire on step complete

  const { playIntro, playDuring, playOutro, stop: stopAudio } = useProtocolAudio();
  const mic = useMicBreath();
  const { requestWakeLock, releaseWakeLock } = useWakeLock();

  const currentProtocol = {
    ...PROTOCOL_STEPS[currentStep],
    duration: PROTOCOL_STEPS[currentStep].id === "yoga_nidra" ? nsdrDuration : PROTOCOL_STEPS[currentStep].duration,
  };
  const phaseColor = getPhaseColor(currentProtocol.phase);

  // First-use guide auto-show — freezes timer until dismissed
  useEffect(() => {
    if (sessionState !== "playing") return;
    if (!getSeenGuides().includes(currentProtocol.id)) {
      guideOpenRef.current = true;
      setShowGuide(true);
    }
  }, [currentStep, sessionState]);

  const handleGuideClose = () => {
    markGuideSeen(currentProtocol.id);
    guideOpenRef.current = false;
    setShowGuide(false);
    // Start mic now if we're in RESET (was held while guide was open)
    if (sessionState === "playing" && currentProtocol.phase === "reset") mic.start();
  };

  // Wake lock
  useEffect(() => {
    if (sessionState === "playing") requestWakeLock();
    return () => releaseWakeLock();
  }, [sessionState]);

  // Mic — only during RESET, and only once guide has been dismissed
  useEffect(() => {
    if (sessionState === "playing" && currentProtocol.phase === "reset" && !guideOpenRef.current) mic.start();
    else mic.stop();
  }, [sessionState, currentStep]);

  // Nostril animation
  useEffect(() => {
    breathTimersRef.current.forEach(clearTimeout);
    breathTimersRef.current = [];
    if (sessionState !== "playing" || currentProtocol.id !== "nadi_shodhana") return;
    const C = 1000;
    let phase = 0, running = true;
    const run = () => {
      if (!running) return;
      const t = breathTimersRef.current;
      switch (phase % 6) {
        case 0: setNostrilSide("left");  setIsSwitch(false); setBreathCyclePhase("inhale"); t.push(setTimeout(()=>{phase++;run();},C*4)); break;
        case 1: setIsSwitch(true);  setBreathCyclePhase("exhale"); t.push(setTimeout(()=>{setIsSwitch(false);setNostrilSide("right");phase++;run();},800)); break;
        case 2: setNostrilSide("right"); setIsSwitch(false); setBreathCyclePhase("exhale"); t.push(setTimeout(()=>{phase++;run();},C*4)); break;
        case 3: setNostrilSide("right"); setIsSwitch(false); setBreathCyclePhase("inhale"); t.push(setTimeout(()=>{phase++;run();},C*4)); break;
        case 4: setIsSwitch(true);  setBreathCyclePhase("exhale"); t.push(setTimeout(()=>{setIsSwitch(false);setNostrilSide("left");phase++;run();},800)); break;
        case 5: setNostrilSide("left");  setIsSwitch(false); setBreathCyclePhase("exhale"); t.push(setTimeout(()=>{phase++;run();},C*4)); break;
        default: break;
      }
    };
    run();
    return () => { running=false; breathTimersRef.current.forEach(clearTimeout); };
  }, [sessionState, currentStep]);

  // Breath cycle phase — drives inhale/exhale direction in NostrilFlow
  // Each step has its own known rhythm. This runs independently of the mic.
  useEffect(() => {
    if (sessionState !== "playing" || currentProtocol.phase !== "reset") return;
    let running = true;
    const timers = [];

    const cycle = (inhaleMs, exhaleMs) => {
      const run = () => {
        if (!running) return;
        setBreathCyclePhase("inhale");
        timers.push(setTimeout(() => {
          if (!running) return;
          setBreathCyclePhase("exhale");
          timers.push(setTimeout(() => { if (running) run(); }, exhaleMs));
        }, inhaleMs));
      };
      run();
    };

    switch (currentProtocol.id) {
      case "kapalbhati":  cycle(600, 600);    break; // fast, roughly equal
      case "nadi_shodhana": break;             // driven by nostril sequencer below
      case "bhramari":    cycle(3000, 6000);  break; // 3 inhale, 6 hum/exhale
      case "rechaka":     cycle(4000, 8000);  break; // 4 inhale, 8 exhale
      default: break;
    }

    return () => { running = false; timers.forEach(clearTimeout); };
  }, [sessionState, currentStep]);

  // For Nadi Shodhana: sync breathCyclePhase to the nostril sequencer
  // (nostrilSide changes on each inhale/exhale — we track the switch moments)
  useEffect(() => {
    if (currentProtocol.id !== "nadi_shodhana") return;
    if (isSwitch) {
      setBreathCyclePhase("exhale"); // switching = end of one breath phase
    }
  }, [isSwitch]);
  useEffect(() => {
    if (sessionState !== "playing") return;
    const id = setInterval(() => {
      if (isIntroPlayingRef.current || guideOpenRef.current) return;
      setStepTimeLeft(prev => { if (prev<=1){ handleStepComplete(); return 0; } return prev-1; });
      setSessionElapsed(prev => prev+1);
    }, 1000);
    return () => clearInterval(id);
  }, [sessionState, currentStep]);

  // Voice on step change
  const prevStepRef = useRef(currentStep);
  useEffect(() => {
    if (sessionState === "playing" && currentStep !== prevStepRef.current) {
      prevStepRef.current = currentStep;
      isCompletingRef.current = false; // reset for new step
      // Skip voice for yoga_nidra — handleNsdrSelected triggers it with correct duration
      const step = PROTOCOL_STEPS[currentStep];
      if (step?.id === "yoga_nidra") { return; }
      stopAudio(); setIsAudioLocked(false);
      if (!isMuted) { setIntroPlaying(true); setTimeout(()=>startVoiceGuidance(), 400); }
      else setIntroPlaying(false);
    }
    prevStepRef.current = currentStep;
  }, [currentStep, sessionState, isMuted]);

  const setIntroPlaying = (val) => { isIntroPlayingRef.current=val; setIsIntroPlaying(val); };

  const triggerTransition = async (playChime = true) => {
    setAmberPulse(true);
    setTimeout(()=>setAmberPulse(false), 900);
    if (!isMuted && playChime) await playBell(3);
  };

  const startVoiceGuidance = async (overrideDuration, overrideStepId) => {
    if (isAudioLocked || isMuted) return;
    setIsAudioLocked(true); setIntroPlaying(true);
    const dur = overrideDuration || nsdrDuration;
    const stepId = overrideStepId || currentProtocol.id;
    try {
      await playIntro(stepId);
      setIntroPlaying(false);
      await new Promise(r=>setTimeout(r,1000));
      await playDuring(stepId, dur);
      await new Promise(r=>setTimeout(r,1000));
      await playOutro(stepId);
    } catch(e) { console.warn("Voice guidance interrupted:",e?.message); }
    finally { setIntroPlaying(false); setIsAudioLocked(false); }
  };

  const handleStepComplete = useCallback(async (fromSkip = false) => {
    if (isCompletingRef.current) return;
    isCompletingRef.current = true;
    mic.stop();
    setCompletedSteps(prev=>[...prev, currentProtocol.id]);
    if (currentStep < PROTOCOL_STEPS.length-1) {
      const next = currentStep+1;
      await triggerTransition(!fromSkip);
      isCompletingRef.current = false;
      if (PROTOCOL_STEPS[next].id === "yoga_nidra") { setSessionState("nsdr_select"); return; }
      setCurrentStep(next);
      setStepTimeLeft(PROTOCOL_STEPS[next].duration);
      // Continuous mode: auto-start next step immediately
      if (continuousMode) {
        setSessionState("playing");
      }
    } else {
      if (!isMuted) await playBell(1);
      isCompletingRef.current = false;
      setSessionState("complete");
      saveCompletion();
    }
  }, [currentStep, currentProtocol, isMuted, continuousMode]);

  const handleNsdrSelected = (duration) => {
    setNsdrDuration(duration);
    const idx = PROTOCOL_STEPS.findIndex(s=>s.id==="yoga_nidra");
    setCurrentStep(idx); setStepTimeLeft(duration); setSessionState("playing");
    if (!isMuted) { setIntroPlaying(true); setTimeout(()=>startVoiceGuidance(duration, "yoga_nidra"), 400); }
  };

  const getPhaseProgress = () => {
    let r=0,l=0,i=0;
    completedSteps.forEach(id=>{
      const s=PROTOCOL_STEPS.find(s=>s.id===id); if(!s) return;
      const d=s.id==="yoga_nidra"?nsdrDuration:s.duration;
      if(s.phase==="reset") r+=d; else if(s.phase==="lock") l+=d; else i+=d;
    });
    const cd=currentProtocol.duration-stepTimeLeft;
    if(currentProtocol.phase==="reset") r+=cd; else if(currentProtocol.phase==="lock") l+=cd; else i+=cd;
    return { reset:Math.min(100,(r/PHASES.reset.duration)*100), lock:Math.min(100,(l/PHASES.lock.duration)*100), install:Math.min(100,(i/(nsdrDuration||PHASES.install.duration))*100) };
  };

  const handleBack = () => {
    stopAudio(); mic.stop();
    if (currentStep>0) { const p=currentStep-1; setCurrentStep(p); setStepTimeLeft(PROTOCOL_STEPS[p].duration); setCompletedSteps(s=>s.filter(id=>id!==PROTOCOL_STEPS[p].id)); }
    else { setSessionState("intro"); setStepTimeLeft(PROTOCOL_STEPS[0].duration); }
  };

  const handleSkip = () => {
    stopAudio();
    handleStepComplete(true);
  };

  const togglePlayPause = () => {
    if (sessionState==="intro") {
      if (!isMuted) {
        // Show 3 → 2 → 1 countdown visually, 1 bell on each beat
        setCountdownNum(3); playBell(1);
        setTimeout(() => { setCountdownNum(2); playBell(1); }, 1000);
        setTimeout(() => {
          setCountdownNum(1); playBell(1);
          setTimeout(() => {
            setCountdownNum(null);
            setSessionState("playing"); setStartTime(Date.now());
            setIntroPlaying(true); setTimeout(() => startVoiceGuidance(), 300);
          }, 900);
        }, 2000);
      } else {
        setCountdownNum(null);
        setSessionState("playing"); setStartTime(Date.now());
        setIntroPlaying(false);
      }
    } else if (sessionState==="playing") {
      setSessionState("paused"); stopAudio(); mic.stop(); setIntroPlaying(false);
    } else if (sessionState==="paused") {
      setSessionState("playing");
      if (!isMuted) { setIntroPlaying(true); setTimeout(()=>startVoiceGuidance(), 300); }
      else setIntroPlaying(false);
    }
  };

  const saveCompletion = async () => {
    try {
      const dur = startTime ? Math.round((Date.now()-startTime)/1000) : 0;
      await axios.post(`${API}/completions`,{duration_seconds:dur,steps_completed:completedSteps,notes:null},{withCredentials:true});
      toast.success("Protocol recorded!");
    } catch { toast.error("Failed to save completion"); }
  };

  const handleSaveJournal = async () => {
    if (!journalEntry.trim()) { navigate("/progress"); return; }
    setSavingJournal(true);
    try {
      await axios.post(`${API}/journal/quick-note`,{content:journalEntry.trim()},{withCredentials:true});
      toast.success("Journal entry saved!"); navigate("/progress");
    } catch { toast.error("Failed to save journal entry"); }
    finally { setSavingJournal(false); }
  };

  // ── INTRO ──────────────────────────────────────────────────────────────
  if (sessionState==="intro") return (
    <div className="min-h-screen flex flex-col" style={{background:"var(--bg)"}} data-testid="protocol-intro">
      {/* COUNTDOWN OVERLAY */}
      <AnimatePresence>
        {countdownNum !== null && (
          <motion.div
            key={countdownNum}
            initial={{opacity:0, scale:1.6}}
            animate={{opacity:1, scale:1}}
            exit={{opacity:0, scale:0.7}}
            transition={{duration:0.25, ease:"easeOut"}}
            className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
            <span style={{
              fontFamily:"'DM Serif Display',serif",
              fontSize:120,
              color:"rgba(74,144,217,0.85)",
              textShadow:"0 0 60px rgba(74,144,217,0.4)",
              lineHeight:1
            }}>{countdownNum}</span>
          </motion.div>
        )}
      </AnimatePresence>
      <header className="flex items-center justify-between px-5 pt-5 pb-3">
        <button onClick={()=>navigate("/protocol")} className="w-8 h-8 rounded-full flex items-center justify-center" style={{background:"rgba(255,255,255,0.06)"}}>
          <ChevronLeft className="w-4 h-4" style={{color:"rgba(237,232,223,0.5)"}}/>
        </button>
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase" style={{color:"rgba(237,232,223,0.3)"}}>Morning Protocol</p>
        <div className="w-8"/>
      </header>
      <main className="flex-1 flex flex-col items-center justify-center px-7 pb-10">
        <motion.div initial={{opacity:0,y:16}} animate={{opacity:1,y:0}} className="text-center max-w-[340px]">
          <h1 className="text-[28px] mb-2" style={{fontFamily:"'DM Serif Display',serif",color:"var(--text)"}}>The Protocol</h1>
          <p className="text-[13px] mb-10" style={{color:"rgba(237,232,223,0.5)",lineHeight:1.65,maxWidth:280,margin:"0 auto 40px"}}>
            A 15-minute sequence to reset your nervous system, open neuroplastic windows, and consolidate new neural pathways.
          </p>
          <div className="flex justify-center gap-8 mb-12">
            {Object.entries(PHASES).map(([key,phase])=>(
              <div key={key} className="text-center">
                <div className="w-2 h-2 rounded-full mx-auto mb-3" style={{background:phase.color,boxShadow:`0 0 8px ${phase.color}60`}}/>
                <p className="font-mono text-[9px] tracking-[0.15em] uppercase" style={{color:phase.color}}>{phase.label}</p>
                <p className="text-[10px] mt-0.5" style={{color:"rgba(237,232,223,0.35)"}}>{key==="install"?"7-15":Math.floor(phase.duration/60)} min</p>
              </div>
            ))}
          </div>
          <button onClick={togglePlayPause} className="w-16 h-16 rounded-full flex items-center justify-center mx-auto transition-transform hover:scale-105 active:scale-95" style={{background:"#4A90D9",boxShadow:"0 0 32px rgba(74,144,217,0.25)"}} data-testid="start-protocol-btn">
            <Play className="w-6 h-6 text-white ml-1" fill="white"/>
          </button>
          <p className="text-[11px] mt-3" style={{color:"rgba(237,232,223,0.3)"}}>Tap to begin</p>

          {/* Continuous mode toggle */}
          <button
            onClick={()=>setContinuousMode(m=>!m)}
            className="mt-6 flex items-center gap-2.5 mx-auto px-4 py-2 rounded-full transition-all"
            style={{
              background: continuousMode ? "rgba(74,144,217,0.12)" : "rgba(255,255,255,0.04)",
              border: `1px solid ${continuousMode ? "rgba(74,144,217,0.35)" : "rgba(255,255,255,0.08)"}`,
            }}
          >
            <div style={{
              width: 28, height: 16, borderRadius: 8,
              background: continuousMode ? "#4A90D9" : "rgba(255,255,255,0.12)",
              position: "relative", transition: "background .2s", flexShrink: 0,
            }}>
              <div style={{
                width: 12, height: 12, borderRadius: "50%", background: "#fff",
                position: "absolute", top: 2,
                left: continuousMode ? 14 : 2,
                transition: "left .2s",
              }}/>
            </div>
            <span className="font-mono text-[10px] tracking-[0.14em] uppercase"
              style={{color: continuousMode ? "rgba(74,144,217,0.9)" : "rgba(237,232,223,0.35)"}}>
              Continuous mode
            </span>
          </button>
          <p className="text-[10px] mt-1.5 text-center" style={{color:"rgba(237,232,223,0.22)"}}>
            Auto-advance through all steps without tapping
          </p>
        </motion.div>
      </main>
    </div>
  );

  // ── NSDR SELECT ────────────────────────────────────────────────────────
  if (sessionState==="nsdr_select") return (
    <div className="min-h-screen flex flex-col" style={{background:"var(--bg)"}}>
      <header className="flex items-center px-5 pt-5 pb-3 h-16">
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase mx-auto" style={{color:"#4CAF7A"}}>INSTALL phase</p>
      </header>
      <main className="flex-1 flex flex-col justify-center"><NSDRSelector onSelect={handleNsdrSelected}/></main>
    </div>
  );

  // ── COMPLETE ───────────────────────────────────────────────────────────
  if (sessionState==="complete") return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{background:"var(--bg)"}} data-testid="protocol-complete">
      <motion.div initial={{opacity:0,scale:0.9}} animate={{opacity:1,scale:1}} className="text-center max-w-md w-full">
        {!showJournalForm ? (<>
          <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center" style={{background:"rgba(76,175,122,0.15)"}}>
            <Check className="w-10 h-10" style={{color:"#4CAF7A"}}/>
          </div>
          <h1 className="text-[28px] mb-4" style={{fontFamily:"'DM Serif Display',serif",color:"var(--text)"}}>Protocol Complete</h1>
          <p className="text-[13px] mb-2" style={{color:"rgba(237,232,223,0.5)"}}>Your session has been recorded.</p>
          <p className="text-[13px] mb-8" style={{color:"#C9A84C"}}>Want to capture today's insights?</p>
          <div className="flex justify-center gap-6 mb-8">
            {Object.entries(PHASES).map(([key,phase])=>(
              <div key={key} className="text-center">
                <div className="w-10 h-10 mx-auto mb-2 rounded-xl flex items-center justify-center" style={{background:`${phase.color}20`}}>
                  <Check className="w-5 h-5" style={{color:phase.color}}/>
                </div>
                <p className="font-mono text-[9px] tracking-[0.1em]" style={{color:phase.color}}>{phase.label}</p>
              </div>
            ))}
          </div>
          <div className="space-y-4">
            <Button onClick={()=>setShowJournalForm(true)} className="w-full py-4 text-[14px] font-medium rounded-[10px]" style={{background:"#4CAF7A",color:"#0a0c0f"}} data-testid="add-journal-btn">Add Journal Entry</Button>
            <button onClick={()=>navigate("/progress")} className="text-[12px] hover:opacity-80 transition-opacity" style={{color:"rgba(237,232,223,0.4)"}} data-testid="skip-to-dashboard-btn">Continue →</button>
          </div>
        </>) : (
          <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} className="text-left">
            <button onClick={()=>setShowJournalForm(false)} className="text-[12px] mb-4 flex items-center gap-1 hover:opacity-80" style={{color:"rgba(237,232,223,0.4)"}}>← Back</button>
            <h2 className="text-[22px] mb-2" style={{fontFamily:"'DM Serif Display',serif",color:"var(--text)"}}>Capture Your Insights</h2>
            <p className="text-[12px] mb-6" style={{color:"rgba(237,232,223,0.5)"}}>How do you feel after the protocol? Any thoughts or observations?</p>
            <textarea value={journalEntry} onChange={e=>setJournalEntry(e.target.value)} placeholder="Today's practice felt... I noticed..." className="w-full h-40 p-4 rounded-[10px] text-[13px] resize-none focus:outline-none" style={{background:"var(--surface)",border:"1px solid var(--border)",color:"var(--text)"}} data-testid="journal-textarea"/>
            <div className="space-y-3 mt-4">
              <Button onClick={handleSaveJournal} disabled={savingJournal} className="w-full py-3 text-[14px] font-medium rounded-[10px]" style={{background:"#4CAF7A",color:"#0a0c0f"}} data-testid="save-journal-btn">
                {savingJournal?"Saving...":journalEntry.trim()?"Save & Continue":"Skip & Continue"}
              </Button>
              <button onClick={()=>navigate("/progress")} className="w-full text-[12px] py-2 hover:opacity-80" style={{color:"rgba(237,232,223,0.4)"}}>Continue</button>
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );

  // ── PLAYER ─────────────────────────────────────────────────────────────
  const phaseProgress = getPhaseProgress();
  const isReset   = currentProtocol.phase==="reset";
  const isTrataka = currentProtocol.id==="trataka";
  const isNSDR    = currentProtocol.id==="yoga_nidra";

  return (
    <div className="min-h-screen flex flex-col" style={{background:"var(--bg)"}} data-testid="protocol-player">
      <AmberPulse active={amberPulse}/>

      <AnimatePresence>
        {showGuide&&(<>
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} className="fixed inset-0 z-40" style={{background:"rgba(0,0,0,0.5)"}} onClick={handleGuideClose}/>
          <GuideSheet stepId={currentProtocol.id} onClose={handleGuideClose}/>
        </>)}
      </AnimatePresence>

      {/* NAV */}
      <header className="flex items-center justify-between px-5 pt-5 pb-3 h-16">
        <button onClick={handleBack} className={`w-10 h-10 rounded-full flex items-center justify-center ${currentStep===0?"opacity-30":""}`} style={{background:"rgba(255,255,255,0.06)"}} data-testid="back-step-btn">
          <ChevronLeft className="w-[16px] h-[16px]" style={{color:"rgba(237,232,223,0.5)"}}/>
        </button>
        <div className="text-center">
          <p className="font-mono text-[11px] tracking-[0.18em] uppercase" style={{color:phaseColor}}>{currentProtocol.phaseLabel}</p>
          <p className="text-[12px]" style={{color:"rgba(237,232,223,0.5)"}}>{currentStep+1} / {PROTOCOL_STEPS.length} · {currentProtocol.sanskrit}</p>
        </div>
        {/* Right side: sound toggle + guide */}
        <div className="flex items-center gap-2">
          <button onClick={e=>{e.stopPropagation();if(!isMuted)stopAudio();setIsMuted(m=>!m);}} className="w-10 h-10 rounded-full flex items-center justify-center" style={{background:"rgba(255,255,255,0.06)"}} data-testid="mute-btn" title={isMuted?"Sound off - tap to enable":"Sound on - tap to mute"}>
            {isMuted
              ? <VolumeX className="w-[16px] h-[16px]" style={{color:"rgba(237,232,223,0.25)"}}/>
              : <Volume2 className="w-[16px] h-[16px]" style={{color:"rgba(237,232,223,0.6)"}}/>
            }
          </button>
          <button onClick={e=>{e.stopPropagation();setShowGuide(true);}} className="w-10 h-10 rounded-full flex items-center justify-center" style={{background:"rgba(255,255,255,0.06)"}} data-testid="guide-btn">
            <HelpCircle className="w-[15px] h-[15px]" style={{color:"rgba(237,232,223,0.35)"}}/>
          </button>
        </div>
      </header>

      {/* RAIL */}
      <div className="px-7 mb-2">
        <div className="flex justify-between mb-1">
          {Object.entries(PHASES).map(([key,phase])=>(
            <span key={key} className="font-mono text-[9px] tracking-[0.12em] uppercase"
              style={{color:currentProtocol.phase===key?phase.color:phaseProgress[key]>=100?`${phase.color}60`:"rgba(237,232,223,0.2)"}}>
              {phase.label}
            </span>
          ))}
        </div>
        <div className="h-[2px] rounded-full flex overflow-hidden" style={{background:"rgba(255,255,255,0.06)"}}>
          {[["reset","33.3%"],["lock","20%"],["install","46.7%"]].map(([key,w])=>(
            <div key={key} className="relative" style={{width:w}}>
              <div className="absolute inset-0 transition-all duration-300" style={{width:`${phaseProgress[key]}%`,background:getPhaseColor(key),opacity:currentProtocol.phase===key?0.9:0.5}}/>
            </div>
          ))}
        </div>
        <div className="text-right mt-1">
          <span className="font-mono text-[10px]" style={{color:"rgba(237,232,223,0.3)"}}>
            {formatTime(sessionElapsed)} / {formatTime(300+180+nsdrDuration)}
          </span>
        </div>
      </div>

      {/* PRACTICE AREA */}
      <main className="flex-1 flex flex-col items-center px-7 pt-4 pb-4">
        <AnimatePresence mode="wait">
          <motion.div key={currentStep} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} transition={{duration:0.3}} className="w-full flex flex-col items-center">

            {isTrataka&&<TratakaCandle/>}
            {isNSDR&&<NSDRVisual color={phaseColor}/>}
            {isReset&&(
              <div className="w-full mb-2">
                <NostrilFlow
                  stepId={currentProtocol.id}
                  isPlaying={sessionState === "playing"}
                  cyclePhase={breathCyclePhase}
                  nostrilSide={currentProtocol.nostril ? nostrilSide : null}
                  isSwitch={currentProtocol.nostril ? isSwitch : false}
                  color={phaseColor}
                  amplitude={mic.amplitude}
                />
                {mic.error&&<p className="font-mono text-[9px] text-center mt-1" style={{color:"rgba(237,232,223,0.28)"}}>{mic.error}</p>}
              </div>
            )}

            {/* TIMER */}
            <div className="flex flex-col items-center mb-4">
              <span className="font-mono tracking-[-0.02em]"
                style={{fontSize:42,fontWeight:300,color:isIntroPlaying?"rgba(237,232,223,0.3)":"var(--text)",transition:"color 0.3s"}}
                data-testid="step-timer">
                {formatTime(stepTimeLeft)}
              </span>
              {isIntroPlaying&&<motion.p initial={{opacity:0}} animate={{opacity:1}} className="font-mono text-[11px] tracking-[0.2em] uppercase mt-1" style={{color:phaseColor}}>listen</motion.p>}
              {!isIntroPlaying&&currentProtocol.breathHint&&<p className="font-mono text-[11px] tracking-[0.14em] uppercase mt-1" style={{color:`${phaseColor}80`}}>{currentProtocol.breathHint}</p>}
            </div>

            <h2 className="text-[22px] text-center mb-0.5" style={{fontFamily:"'DM Serif Display',serif",color:"var(--text)"}} data-testid="practice-name">{currentProtocol.name}</h2>
            <p className="text-[13px] italic text-center mb-1" style={{fontFamily:"'DM Serif Display',serif",color:"rgba(237,232,223,0.45)"}}>{currentProtocol.sanskrit}</p>
            <p className="font-mono text-[11px] tracking-[0.08em] text-center mb-5" style={{color:`${phaseColor}80`}}>{currentProtocol.benefit}</p>

            <div className="flex gap-[6px] items-center">
              {PROTOCOL_STEPS.map((step,idx)=>(
                <div key={step.id} className={`transition-all duration-200 ${idx===currentStep?"w-[18px] h-[5px] rounded-[3px]":"w-[5px] h-[5px] rounded-full"}`}
                  style={{background:idx<currentStep?`${getPhaseColor(step.phase)}55`:idx===currentStep?phaseColor:"rgba(255,255,255,0.12)"}}/>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </main>

      {/* CONTROLS */}
      <footer className="px-7 py-4 pb-8 flex items-center justify-between" style={{borderTop:"1px solid rgba(255,255,255,0.06)"}}>
        <button onClick={e=>{e.stopPropagation();handleBack();}} className={`text-[13px] w-14 hover:opacity-80 transition-opacity ${currentStep===0?"opacity-20 pointer-events-none":""}`} style={{color:"rgba(237,232,223,0.4)"}} data-testid="back-label-btn">
          ‹ Back
        </button>
        <button onClick={e=>{e.stopPropagation();togglePlayPause();}} className="w-16 h-16 rounded-full flex items-center justify-center transition-transform hover:scale-105 active:scale-95" style={{background:"#C9A84C",boxShadow:"0 0 28px rgba(201,168,76,0.28)"}} data-testid="play-pause-btn">
          {sessionState==="playing"?<Pause className="w-[20px] h-[20px]" fill="white" style={{color:"white"}}/>:<Play className="w-[20px] h-[20px] ml-0.5" fill="white" style={{color:"white"}}/>}
        </button>
        <button onClick={e=>{e.stopPropagation();handleSkip();}} className="text-[13px] w-14 text-right hover:opacity-80 transition-opacity" style={{color:"rgba(237,232,223,0.4)"}} data-testid="skip-btn">
          Skip ›
        </button>
      </footer>
    </div>
  );
};

export default Protocol;
